import { z } from 'zod';
/**
 * Deterministic, serialisable pseudo-random source (mulberry32).
 * The engine owns one RNG state per module instance, persists it with the instance,
 * and advances it inside the same transaction as the action that consumed it.
 * Live sessions seed from crypto; presenter/test fixtures pass an explicit seed.
 * Clients never supply trusted random values.
 * Each draw hashes (seed, counter) through one mulberry32 round: adequate for dice and shuffles, not for statistics.
 */
export const RngState = z.object({ seed: z.number().int().nonnegative(), counter: z.number().int().nonnegative() });
export type RngState = z.infer<typeof RngState>;

function mulberry32(a: number): number {
  let t = (a += 0x6d2b79f5) >>> 0;
  t = Math.imul(t ^ (t >>> 15), t | 1);
  t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
}

export function createRng(initial: RngState): {
  next: () => number;
  nextInt: (minInclusive: number, maxInclusive: number) => number;
  state: () => RngState;
} {
  let state: RngState = { seed: initial.seed >>> 0, counter: initial.counter };
  const next = (): number => {
    // Each draw derives from seed + counter so the state is a compact, serialisable pair.
    const value = mulberry32((state.seed + Math.imul(state.counter + 1, 0x9e3779b1)) >>> 0);
    state = { seed: state.seed, counter: state.counter + 1 };
    return value;
  };
  return {
    next,
    nextInt: (minInclusive, maxInclusive) => {
      const span = maxInclusive - minInclusive + 1;
      return minInclusive + Math.floor(next() * span);
    },
    state: () => ({ ...state }),
  };
}
